import { FC } from 'react'
// Types
import { DistributionMethods } from './types/types'
import { Petition, Drop, PetitionMetadata } from 'backoffice/components/DropRequests/usePetitionsQuery'

// Utils
import { formatDate, DATE_FORMAT_MASK_WITH_TIME, DATE_FORMAT_MASK_WITH_TIMEZONE } from 'lib/date'

// Assets
import { ReactComponent as CalendarEndIcon } from 'images/icons/calendar-end.svg'
import { ReactComponent as AnotationIcon } from 'images/icons/annotation.svg'
import { ReactComponent as EyeIcon } from 'images/icons/eye.svg'

// Styles
import styles from './PetitionMetadata.module.scss'

type WebsiteSecretProps = {
  metadata: PetitionMetadata | undefined
}

type PetitionInfoProps = {
  petition: Petition
}

const MintLink = ({ drop }: { drop: Drop }) => {
  if (!drop?.expiry_date) {
    return null
  }

  const expireDate = formatDate(drop.expiry_date, DATE_FORMAT_MASK_WITH_TIME)

  return (
    <div className={styles.infoItem}>
      <CalendarEndIcon className={styles.icon} />
      <div aria-label="Expiration Date" data-cooltipz-dir="top">
        <div className={styles.date}>{expireDate}</div>
      </div>
    </div>
  )
}

const WebsiteSecret: FC<WebsiteSecretProps> = ({ metadata }) => {
  if (!metadata) return null

  const isDefined = (field: any) => typeof field !== 'undefined'
  const getFieldStatusText = (field: any) => isDefined(field) && (field ? 'Enabled' : 'Disabled')

  const { valid_from, valid_to, password, is_active } = metadata

  const status = getFieldStatusText(is_active)

  return (
    <>
      {valid_from && valid_to && (
        <div className={styles.infoItem}>
          <CalendarEndIcon className={styles.icon} />
          <div aria-label="Distribution Duration" data-cooltipz-dir="top">
            <span className={styles.date}>{formatDate(valid_from, DATE_FORMAT_MASK_WITH_TIME)} to</span>{' '}
            <span className={styles.date}>{formatDate(valid_to, DATE_FORMAT_MASK_WITH_TIMEZONE)}</span>
          </div>
        </div>
      )}
      {password && (
        <div className={styles.infoItem}>
          <AnotationIcon className={styles.icon} />
          <span className={styles.date}>Name: {password}</span>
        </div>
      )}
      {status && (
        <div className={styles.infoItem}>
          <EyeIcon className={styles.icon} />
          <span className={styles.date}>Status: {status}</span>
        </div>
      )}
    </>
  )
}

const PetitionInfo: FC<PetitionInfoProps> = ({ petition }) => {
  const { drop, distribution_method } = petition
  const hasMetadata = drop.expiry_date || petition.website_metadata || petition.secret_metadata

  if (distribution_method === DistributionMethods.DELIVERY || !hasMetadata) return null

  return (
    <>
      <hr className={styles.hr} />
      {distribution_method === DistributionMethods.MINT_LINK ? (
        <MintLink drop={drop} />
      ) : (
        <WebsiteSecret metadata={petition.website_metadata ?? petition.secret_metadata} />
      )}
    </>
  )
}

export default PetitionInfo
